import React from 'react';
import { trackEvent } from '../../utils/analytics/analytics';
import { convertUrlToLanguage } from '../../utils/algolia/url-handling';
import { normalizeSupportedLocale } from '../../utils/routing/locale-from-pathname';
import type { IntlShape } from 'react-intl';
import {
  CORE_LANG_SEGMENT_CONTAINER,
  CORE_LANG_SEGMENT_ITEM,
  CORE_LANG_SEGMENT_ACTIVE,
  CORE_LANG_SEGMENT_INACTIVE,
} from '../../constants/corePublicTheme';

export interface LanguageSelectorProps {
  intl: IntlShape;
  /** Current locale; if missing or invalid, falls back to intl.locale so the button is never empty */
  locale?: 'en' | 'fi' | 'sv';
  className?: string;
}

/**
 * Language selector component for navigation.
 * Shows FI/SV/EN side by side as a segmented control, matching the selector
 * used on the homepage, SRP header, and resources pages.
 * Only visible on xl screens and above.
 */
export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  intl,
  locale: localeProp,
  className = '',
}) => {
  // Ensure we always have a valid display locale (static SSR may pass undefined; hydration may pass wrong value)
  const locale = normalizeSupportedLocale(localeProp ?? intl?.locale);

  const handleLanguageChange = (newLocale: 'en' | 'fi' | 'sv') => {
    trackEvent({
      name: 'locale_change',
      params: {
        to_locale: newLocale,
        from_locale: locale,
        surface: 'other',
      },
    });
    window.location.href = convertUrlToLanguage(window.location.pathname, newLocale, intl);
  };

  return (
    <div className={`hidden xl:flex ${CORE_LANG_SEGMENT_CONTAINER} ${className}`}>
      {(['fi', 'sv', 'en'] as const).map((lang) => (
        <button
          key={lang}
          type="button"
          onClick={() => handleLanguageChange(lang)}
          aria-current={locale === lang ? 'true' : undefined}
          aria-label={intl.formatMessage({
            id: 'navigation.language',
            defaultMessage: 'Language',
            description: 'Button aria-label for language selector in navigation',
          })}
          className={`cursor-pointer ${CORE_LANG_SEGMENT_ITEM} ${
            locale === lang ? CORE_LANG_SEGMENT_ACTIVE : CORE_LANG_SEGMENT_INACTIVE
          }`}
        >
          {lang.toUpperCase()}
        </button>
      ))}
    </div>
  );
};
